import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  Loader2,
  BookMarked,
  ArrowLeft,
  Copy,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  AlertOctagon,
  HelpCircle,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import {
  generateBibliography,
  verifyBibliographySources,
  saveCitation,
} from "@/lib/api/ai-writing.functions";
import { CITATION_STYLES } from "@/lib/citation-styles";

export const Route = createFileRoute("/education/writing/bibliography")({
  head: () => ({
    meta: [
      { title: "Bibliography & Reference Generator — Legal Writing | Nkadimeng Danny" },
      {
        name: "description",
        content:
          "Verify your sources for credibility and completeness, then generate a clean bibliography, reference list or footnote list in OSCOLA, Harvard, APA 7 or South African legal citation. Save every output to My Citations.",
      },
      { property: "og:title", content: "Bibliography & Reference Generator" },
    ],
  }),
  component: BibliographyPage,
});

type Mode = "bibliography" | "reference_list" | "footnote_list";
type Style = "oscola" | "harvard" | "apa-7" | "sa-citation";

const MODES: { value: Mode; label: string; hint: string }[] = [
  { value: "bibliography", label: "Bibliography", hint: "Grouped by source type, alphabetised by author." },
  { value: "reference_list", label: "Reference list", hint: "Single alphabetised list — APA/Harvard style." },
  { value: "footnote_list", label: "Footnote list", hint: "Numbered 1, 2, 3 … in the order you list sources." },
];

type VerifyItem = {
  input: string;
  credibility?: "credible" | "questionable" | "non_credible" | "unknown";
  missing?: string[];
  suggested_correction?: string | null;
  notes?: string;
};

function BibliographyPage() {
  const runGen = useServerFn(generateBibliography);
  const runVerify = useServerFn(verifyBibliographySources);
  const runSave = useServerFn(saveCitation);

  const [style, setStyle] = useState<Style>("sa-citation");
  const [mode, setMode] = useState<Mode>("bibliography");
  const [sources, setSources] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifyItems, setVerifyItems] = useState<VerifyItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [out, setOut] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [bypassed, setBypassed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [title, setTitle] = useState("");

  function handleError(e: any) {
    const msg = String(e?.message ?? "");
    if (msg.includes("INSUFFICIENT_CREDITS"))
      setErr("You've run out of AI credits. Buy more from your portal credits page.");
    else if (msg.includes("RATE_LIMIT")) setErr("Too many requests right now — try again in a minute.");
    else setErr("AI service is unavailable. Try again shortly.");
  }

  async function onVerify() {
    setVerifying(true);
    setErr(null);
    setVerifyItems(null);
    try {
      const r = await runVerify({ data: { sources, style } });
      setBalance(r.balance);
      setBypassed(r.bypassed);
      if (r.ok) setVerifyItems(r.items as VerifyItem[]);
      else setErr(r.error);
    } catch (e) {
      handleError(e);
    } finally {
      setVerifying(false);
    }
  }

  function applySuggestions() {
    if (!verifyItems) return;
    const next = verifyItems
      .map((it) => (it.suggested_correction && it.suggested_correction.trim()) || it.input)
      .join("\n");
    setSources(next);
    toast.success("Suggested corrections applied — review then generate.");
  }

  async function onRun(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    setOut(null);
    setSaved(false);
    try {
      const r = await runGen({ data: { style, mode, sources } });
      setBalance(r.balance);
      setBypassed(r.bypassed);
      if (r.ok) setOut(r.output);
      else setErr(r.error);
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  }

  async function onSave() {
    if (!out) return;
    setSaving(true);
    try {
      await runSave({
        data: {
          title: title.trim() || `${MODES.find((m) => m.value === mode)?.label} — ${new Date().toLocaleDateString()}`,
          kind: mode,
          style,
          inputText: sources,
          outputText: out,
          verification: verifyItems ?? [],
        },
      });
      setSaved(true);
      toast.success("Saved to My Citations.");
    } catch (e: any) {
      toast.error(e?.message ?? "Could not save.");
    } finally {
      setSaving(false);
    }
  }

  const nonCredibleCount = (verifyItems ?? []).filter(
    (i) => i.credibility === "non_credible",
  ).length;
  const questionableCount = (verifyItems ?? []).filter(
    (i) => i.credibility === "questionable",
  ).length;

  return (
    <div className="container-narrow py-10">
      <Link to="/education/writing" className="inline-flex items-center gap-1 text-xs text-navy/70 hover:text-navy">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Writing Academy
      </Link>
      <div className="mt-3 flex items-center gap-3">
        <span className="rounded-md bg-navy/5 p-2 text-navy">
          <BookMarked className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-3xl text-navy">Bibliography &amp; Reference Generator</h1>
          <p className="text-sm text-muted-foreground">
            Step 1: verify your sources. Step 2: generate. Step 3: save to{" "}
            <Link to="/portal/my-citations" className="underline">My Citations</Link>. 1 credit per AI step.
          </p>
        </div>
      </div>

      <form onSubmit={onRun} className="mt-6 space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-semibold text-navy">Citation style</label>
            <select
              value={style}
              onChange={(e) => setStyle(e.target.value as Style)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {CITATION_STYLES.map((s) => (
                <option key={s.slug} value={s.slug}>{s.shortName}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold text-navy">Output mode</label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as Mode)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {MODES.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">{MODES.find((m) => m.value === mode)?.hint}</p>
          </div>
        </div>
        <div>
          <label className="text-sm font-semibold text-navy">Source notes</label>
          <textarea
            value={sources}
            onChange={(e) => setSources(e.target.value)}
            required
            minLength={20}
            maxLength={10000}
            rows={10}
            placeholder={`One source per line, e.g.\nS v Makwanyane Constitutional Court 1995\nConstitution of the Republic of South Africa 1996\nCurrie & De Waal 'The Bill of Rights Handbook' 6th ed 2013`}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
          />
          <p className="mt-1 text-xs text-muted-foreground">{sources.length}/10000</p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={onVerify}
            disabled={verifying || sources.trim().length < 10}
            className="inline-flex items-center gap-2 rounded-md border border-navy/30 bg-white px-4 py-2.5 text-sm font-semibold text-navy disabled:opacity-50"
          >
            {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Verify sources first (1 credit)
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-2.5 text-sm font-semibold text-navy-foreground disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookMarked className="h-4 w-4" />}
            Generate {MODES.find((m) => m.value === mode)?.label.toLowerCase()} (1 credit)
          </button>
        </div>
      </form>

      {balance !== null && (
        <p className="mt-3 text-xs text-muted-foreground">
          {bypassed
            ? "Admin — unlimited generations."
            : <>Credits remaining: <strong>{balance}</strong></>}
        </p>
      )}
      {err && <p className="mt-4 rounded-md bg-rose-50 p-3 text-sm text-rose-800">{err}</p>}

      {verifyItems && (
        <section className="mt-6 rounded-lg border border-input bg-card p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-semibold text-navy">Source verification</h2>
              <p className="text-xs text-muted-foreground">
                {verifyItems.length} sources checked · {nonCredibleCount} non-credible · {questionableCount} questionable.
                Format only — always confirm at the primary source.
              </p>
            </div>
            <button
              type="button"
              onClick={applySuggestions}
              className="inline-flex items-center gap-1 rounded-md border border-input px-3 py-1.5 text-xs hover:bg-muted"
            >
              Apply suggested corrections
            </button>
          </div>
          <ul className="mt-4 space-y-3">
            {verifyItems.map((it, idx) => (
              <li key={idx} className="rounded-md border border-border bg-background p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <p className="font-mono text-sm text-navy">{it.input}</p>
                  <CredibilityBadge v={it.credibility} />
                </div>
                {Array.isArray(it.missing) && it.missing.length > 0 && (
                  <p className="mt-1 text-xs text-amber-800">
                    Missing: {it.missing.join(", ")}
                  </p>
                )}
                {it.suggested_correction && (
                  <p className="mt-2 rounded-md bg-emerald-50 p-2 font-mono text-xs text-emerald-900">
                    Suggested: {it.suggested_correction}
                  </p>
                )}
                {it.notes && <p className="mt-1 text-xs text-muted-foreground">{it.notes}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {out && (
        <article className="mt-6 rounded-lg border border-input bg-card p-5">
          <div className="mb-3 flex flex-wrap justify-end gap-2">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title for My Citations…"
              className="rounded-md border border-input bg-background px-2 py-1 text-xs"
            />
            <button
              onClick={onSave}
              disabled={saving || saved}
              className="inline-flex items-center gap-1 rounded-md border border-input px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {saved ? "Saved" : "Save to My Citations"}
            </button>
            <button
              onClick={() => navigator.clipboard.writeText(out)}
              className="inline-flex items-center gap-1 rounded-md border border-input px-3 py-1.5 text-xs hover:bg-muted"
            >
              <Copy className="h-3.5 w-3.5" /> Copy
            </button>
          </div>
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">{out}</pre>
          <p className="mt-4 border-t border-border pt-3 text-xs italic text-muted-foreground">
            AI-generated. Verify every citation against the primary source before relying on it.
          </p>
        </article>
      )}
    </div>
  );
}

function CredibilityBadge({ v }: { v?: string }) {
  if (v === "credible")
    return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-semibold text-emerald-800"><CheckCircle2 className="h-3 w-3" /> Credible</span>;
  if (v === "questionable")
    return <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-[10px] font-semibold text-amber-800"><AlertTriangle className="h-3 w-3" /> Questionable</span>;
  if (v === "non_credible")
    return <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-0.5 text-[10px] font-semibold text-rose-800"><AlertOctagon className="h-3 w-3" /> Non-credible</span>;
  return <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-[10px] font-semibold text-foreground/70"><HelpCircle className="h-3 w-3" /> Unknown</span>;
}
